import { Injectable, signal } from '@angular/core';
import { BaseStore } from '@kerberos-compliance/compliance-shared-ui';
import type { ResponseContractDetailsDto } from '../types/contracts.types';

@Injectable()
export class ContractsDetailStore extends BaseStore<ResponseContractDetailsDto> {
  private readonly contractsRefreshingState = signal<boolean>(false);

  // Identifies the newest fetch of the tab's data. A superseded fetch commits no response and leaves the
  // busy flags to its successor, so a slow reply can neither overwrite a newer reveal toggle's or a
  // different organization's data nor end a newer fetch's loading state early.
  private readonly contractsFetchGenerationState = signal<number>(0);

  public readonly contracts = this.items;

  /**
   * Whether only the contracts are being re-fetched. Distinct from `loading`, which covers the whole tab:
   * a reveal toggle changes nothing outside the contracts list, so the rest of the tab stays put.
   */
  public readonly contractsRefreshing = this.contractsRefreshingState.asReadonly();

  /**
   * Replaces the list of active contracts.
   *
   * @param {ResponseContractDetailsDto[]} contracts - The active contracts to store.
   * @returns {void} This method does not return a value.
   */
  public setContracts(contracts: ResponseContractDetailsDto[]): void {
    this.setItems(contracts);
  }

  /**
   * Sets the loading flag for the tab (delegates to the BaseStore loading signal).
   *
   * @param {boolean} isLoading - Whether data is currently loading.
   * @returns {void} This method does not return a value.
   */
  public setLoadingState(isLoading: boolean): void {
    this.setLoading(isLoading);
  }

  /**
   * Sets the contracts-only refresh flag.
   *
   * @param {boolean} isRefreshing - Whether the contracts list is being re-fetched on its own.
   * @returns {void} This method does not return a value.
   */
  public setContractsRefreshing(isRefreshing: boolean): void {
    this.contractsRefreshingState.set(isRefreshing);
  }

  /**
   * Marks the start of a contracts fetch, superseding any fetch already in flight.
   *
   * @returns {number} The generation identifying this fetch, to be passed to {@link isCurrentContractsFetch}.
   */
  public startContractsFetch(): number {
    const generation = this.contractsFetchGenerationState() + 1;
    this.contractsFetchGenerationState.set(generation);

    return generation;
  }

  /**
   * Whether the given fetch is still the newest one started, i.e. whether its response may be committed.
   *
   * @param {number} generation - The generation returned by {@link startContractsFetch}.
   * @returns {boolean} True while no later fetch has started.
   */
  public isCurrentContractsFetch(generation: number): boolean {
    return this.contractsFetchGenerationState() === generation;
  }

  /**
   * Ends a contracts fetch. The newest fetch settles both busy flags; a superseded one changes nothing and
   * leaves them to its successor, so a slow completion cannot drop a newer fetch's loader early.
   *
   * @param {number} generation - The generation returned by {@link startContractsFetch}.
   * @returns {void} This method does not return a value.
   */
  public endContractsFetch(generation: number): void {
    if (!this.isCurrentContractsFetch(generation)) {
      return;
    }

    this.setLoading(false);
    this.contractsRefreshingState.set(false);
  }

  /**
   * Clears all contract state. Called when the organization changes. Bumping the fetch generation invalidates
   * anything already in flight, so a late response cannot repopulate what was cleared.
   *
   * @returns {void} This method does not return a value.
   */
  public clearAll(): void {
    this.resetState();
    this.contractsRefreshingState.set(false);
    this.startContractsFetch();
    this.setLoading(false);
  }
}
